import { useMemo } from 'react';
import { Store } from '@geely/shared';
import { useAvailability } from '@/hooks/useAppointments';
import { useGeelyCareWeekendDates } from '@/hooks/useSpecialDateHours';
import {
  getAvailableDates,
  formatDateForSelect
} from '@/lib/utils';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
} from "@geely/ui";
import { Clock, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DateCarousel } from './DateCarousel';

interface DateTimeSchedulerProps {
  selectedStoreId: string;
  selectedDate: string;
  selectedTime: string;
  stores: Store[];
  onSelectDateTime: (date: string, time: string) => void;
}

export function DateTimeScheduler({
  selectedStoreId,
  selectedDate,
  selectedTime,
  stores,
  onSelectDateTime
}: DateTimeSchedulerProps) {
  const availableDates = getAvailableDates(60); // Get next 60 days

  const { data: gcwDatesList = [], isLoading: isLoadingGcw } = useGeelyCareWeekendDates(selectedStoreId || undefined);

  const gcwDateSet = useMemo(() => new Set(gcwDatesList), [gcwDatesList]);
  const hasGcw = gcwDateSet.size > 0;

  const gcwDates = useMemo(
    () => hasGcw ? availableDates.filter(d => gcwDateSet.has(d)) : [],
    [availableDates, gcwDateSet, hasGcw]
  );

  const otherDates = useMemo(
    () => hasGcw ? availableDates.filter(d => !gcwDateSet.has(d)) : availableDates,
    [availableDates, gcwDateSet, hasGcw]
  );

  // Auto-select first date if none selected
  const currentDate = selectedDate || availableDates[0];

  // CAMADA 2: Fetch availability from API with aggressive polling
  // Only fetch if storeId is selected
  const { data: availabilityData, isLoading, dataUpdatedAt, refetch } = useAvailability(
    selectedStoreId || '',
    currentDate,
    { enabled: !!selectedStoreId }
  );

  // Transform API data into time slots format
  const timeSlots = useMemo(() => {
    if (!availabilityData?.slots) return [];
    return availabilityData.slots.map(slot => ({
      time: slot.time,
      available: slot.available > 0,
      count: slot.available,
      total: slot.total
    }));
  }, [availabilityData]);

  const handleDateClick = (date: string) => {
    // Reset time when changing date
    onSelectDateTime(date, '');
  };

  const handleTimeClick = (time: string, available: boolean) => {
    if (!available) return;
    onSelectDateTime(currentDate, time);
  };

  const hasAvailableSlots = timeSlots.some(slot => slot.available);

  return (
    <div className="space-y-4">
      {/* Day Cards */}
      {isLoadingGcw ? (
        <div className="flex flex-col items-center justify-center py-12 text-[#64748b]">
          <Loader2 className="size-8 animate-spin mb-3" />
          <p className="text-sm">Carregando datas disponíveis...</p>
        </div>
      ) : hasGcw ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-[#e2e8f0] p-4">
            <DateCarousel
              title="Geely Care Weekend"
              dates={gcwDates}
              currentDate={currentDate}
              onDateClick={handleDateClick}
              compact
            />
          </div>
          <div className="rounded-lg border border-[#e2e8f0] p-4">
            <DateCarousel
              title="Outros dias"
              dates={otherDates}
              currentDate={currentDate}
              onDateClick={handleDateClick}
              compact
            />
          </div>
        </div>
      ) : (
        <DateCarousel
          title="Escolha o Dia"
          dates={otherDates}
          currentDate={currentDate}
          onDateClick={handleDateClick}
        />
      )}

      {/* Time Slots Grid */}
      {currentDate && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-[#080404]">Escolha o Horário</h3>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-[#64748b]">
                <Clock className="size-3" />
                <span>{formatDateForSelect(currentDate)}</span>
              </div>
            </div>
          </div>

          {/* CAMADA 4: Last update timestamp and manual refresh */}
          <div className="flex items-center justify-between mb-3 p-2 bg-blue-50 rounded-md border border-blue-200">
            <div className="flex items-center gap-2 text-xs text-blue-700">
              <AlertCircle className="size-3" />
              <span>
                Atualizado {new Date(dataUpdatedAt).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="h-7 text-xs gap-1"
              disabled={isLoading}
            >
              <RefreshCw className={cn("size-3", isLoading && "animate-spin")} />
              Atualizar agora
            </Button>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="size-6 animate-spin text-[#64748b]" />
              <span className="ml-2 text-sm text-[#64748b]">Carregando horários disponíveis...</span>
            </div>
          )}

          {!isLoading && !hasAvailableSlots && (
            <Alert>
              <AlertDescription>
                Não há horários disponíveis para esta data. Por favor, escolha outra data.
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && hasAvailableSlots && (
            <div className="space-y-4">
              {/* Time Slots Grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {timeSlots.map(({ time, available, count, total }) => {
                  const isSelected = selectedTime === time;
                  const isLowAvailability = available && count <= 2; // CAMADA 4: Pressure indicator
                  const isCritical = available && count === 1; // CAMADA 4: Critical pressure

                  return (
                    <div key={time} className="relative">
                      <button
                        disabled={!available}
                        onClick={() => handleTimeClick(time, available)}
                        className={cn(
                          "w-full px-3 py-2 rounded-md text-sm font-medium transition-all",
                          "border focus:outline-none focus:ring-2 focus:ring-offset-2",
                          isSelected && available && [
                            "bg-[#000000] text-white border-[#000000]",
                            "focus:ring-[#000000]"
                          ],
                          !isSelected && available && [
                            "bg-white text-[#080404] border-[#e2e8f0]",
                            "hover:bg-[#000000]/5 hover:border-[#000000]/30",
                            "focus:ring-[#000000]/50"
                          ],
                          !available && [
                            "bg-[#e2e8f0] text-[#94a3b8] border-[#e2e8f0]",
                            "cursor-not-allowed opacity-60"
                          ]
                        )}
                      >
                        <span className="block">{time}</span>
                        {/* CAMADA 4: Enhanced pressure badges */}
                        {isLowAvailability && !isSelected && (
                          <Badge
                            variant={isCritical ? "destructive" : "secondary"}
                            className={cn(
                              "mt-1 text-[10px] px-1 py-0 h-4",
                              isCritical && "bg-red-500 text-white animate-pulse"
                            )}
                          >
                            {count === 1 ? '1 vaga!' : `${count} vagas`}
                          </Badge>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 pt-2 border-t border-[#e2e8f0]">
                <div className="flex items-center gap-2 text-xs text-[#64748b]">
                  <div className="w-3 h-3 rounded border border-[#e2e8f0] bg-white" />
                  <span>Disponível</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#64748b]">
                  <div className="w-3 h-3 rounded bg-[#e2e8f0]" />
                  <span>Ocupado</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#64748b]">
                  <div className="w-3 h-3 rounded bg-[#000000]" />
                  <span>Selecionado</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
